import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Badge,
    Box,
    Button,
    HStack,
    IconButton,
    Spinner,
    Text,
    VStack,
} from "@chakra-ui/react";
import { HiArrowDownTray, HiOutlineClipboard } from "react-icons/hi2";
import WorkflowService from "@/services/agency/WorkflowService";
import JobOfferFormModal from "@/components/jobOffer/JobOfferFormModal";
import { fromApiResponse, toUpdateRequest } from "@/services/jobOfferMapper";
import {useChat} from "@/components/providers/ChatProvider";
import { SCORING_ENABLED } from "@/scoringUi";

const InfoField = ({ label, children }) => (
    <VStack align="flex-start" gap={1}>
        <Text color="text.grey" fontSize="xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider">
            {label}
        </Text>
        {children}
    </VStack>
);

const SectionCard = ({ title, children }) => (
    <VStack w="full" align="flex-start" gap={3}>
        <Text fontWeight={700} fontSize="lg" color="white">{title}</Text>
        <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
            {children}
        </Box>
    </VStack>
);

const StringList = ({ items }) => {
    if (!items?.length) return <Text color="text.grey" fontSize="sm">—</Text>;
    return (
        <VStack align="flex-start" gap={1}>
            {items.map((item, i) => (
                <Text key={i} color="white" fontSize="sm">{i + 1}. {item}</Text>
            ))}
        </VStack>
    );
};

const TagList = ({ items }) => {
    if (!items?.length) return <Text color="text.grey" fontSize="sm">—</Text>;
    return (
        <HStack gap={2} flexWrap="wrap">
            {items.map((item, i) => (
                <Badge key={i} colorPalette="brand" variant="subtle" fontSize="xs">{item}</Badge>
            ))}
        </HStack>
    );
};

const StatusBadge = ({ status }) => {
    const colorMap = { active: "green", closed: "red", draft: "gray" };
    const color = colorMap[status?.toLowerCase()] ?? "orange";
    return (
        <Badge colorPalette={color} variant="solid" fontSize="xs" px={3} py={0.5} borderRadius={6}>
            {status ?? "—"}
        </Badge>
    );
};

const parseJson = (value) => {
    if (value == null) return null;
    if (Array.isArray(value) && value.length === 0) return null;
    try {
        return typeof value === "string" ? JSON.parse(value) : value;
    } catch {
        return null;
    }
};

const JsonSection = ({ title, data, downloadFilename }) => {
    const parsed = parseJson(data);
    const json = parsed ? JSON.stringify(parsed, null, 2) : null;

    const handleCopy = () => navigator.clipboard.writeText(json);

    const handleDownload = () => {
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = downloadFilename;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <VStack w="full" align="flex-start" gap={3}>
            <HStack w="full" justify="space-between">
                <Text fontWeight={700} fontSize="lg" color="white">{title}</Text>
                {json && (
                    <HStack gap={2}>
                        <IconButton variant="ghost" size="sm" aria-label="Kopiuj JSON" onClick={handleCopy} color="text.grey" _hover={{ color: "white" }}>
                            <HiOutlineClipboard />
                        </IconButton>
                        <IconButton variant="ghost" size="sm" aria-label="Pobierz JSON" onClick={handleDownload} color="text.grey" _hover={{ color: "white" }}>
                            <HiArrowDownTray />
                        </IconButton>
                    </HStack>
                )}
            </HStack>
            <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5} overflow="hidden">
                {json ? (
                    <Box
                        as="pre"
                        bg="bg.secondary"
                        borderRadius={8}
                        overflow="auto"
                        p={4}
                        whiteSpace="break-spaces"
                        maxH="400px"
                        fontSize="sm"
                        fontFamily="mono"
                        color="white"
                    >
                        {json}
                    </Box>
                ) : (
                    <Text color="text.grey" fontSize="sm">Brak danych</Text>
                )}
            </Box>
        </VStack>
    );
};

const WorkflowDetailPage = () => {
    const { workflowId } = useParams();
    const [workflow, setWorkflow] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [editModalValues, setEditModalValues] = useState(null);
    const [isEditLoading, setIsEditLoading] = useState(false);
    const { startNewChat } = useChat();

    const refetchWorkflow = () => {
        setIsLoading(true);
        WorkflowService.getById(workflowId)
            .then(setWorkflow)
            .finally(() => setIsLoading(false));
    };

    const handleEdit = async () => {
        setIsEditLoading(true);
        try {
            const data = await WorkflowService.getById(workflowId);
            setEditModalValues(fromApiResponse(data));
        } finally {
            setIsEditLoading(false);
        }
    };
    
    const handleStartNewChat = () => {
        console.log('[WorkflowDetailPage] 🚀 ', JSON.stringify({scenario: workflow.scenario, recruitment_id: workflow.id}, null, 2));
        startNewChat(workflow.scenario, {recruitment_id: workflow.id}, { ignoreSpecial: true });
    }

    useEffect(() => {
        setIsLoading(true);
        WorkflowService.getById(workflowId)
            .then(setWorkflow)
            .catch(() => setNotFound(true))
            .finally(() => setIsLoading(false));
    }, [workflowId]);

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="brand.400" size="lg" />
            </VStack>
        );
    }

    if (notFound || !workflow) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="text.grey">Nie znaleziono rekrutacji.</Text>
            </VStack>
        );
    }

    return (
        <>
        <VStack w="full" py={6} gap={6} align="flex-start">
            <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                <HStack justify="space-between" align="flex-start" mb={4}>
                    <HStack>
                        <Text fontWeight={700} fontSize="xl" color="white">{workflow.jobTitle}</Text>
                        <Button
                            ml={4}
                            variant="outlined"
                            onClick={handleStartNewChat}
                            textDecoration='unset'
                            size={'xs'}
                            fontSize={12}>
                            <Text>Start new chat</Text>
                        </Button>
                    </HStack>
                    <HStack gap={2}>
                        <StatusBadge status={workflow.status} />
                        <Button size="sm" variant="outline" loading={isEditLoading} onClick={handleEdit}>
                            Edytuj
                        </Button>
                    </HStack>
                </HStack>
                <HStack gap={8} align="flex-start" flexWrap="wrap">
                    <InfoField label="Dział">
                        <Text color="white" fontSize="sm">{workflow.department ?? "—"}</Text>
                    </InfoField>
                    <InfoField label="Firma">
                        <Text color="white" fontSize="sm">{workflow.companyName ?? "—"}</Text>
                    </InfoField>
                    <InfoField label="Lokalizacja">
                        <Text color="white" fontSize="sm">{workflow.location ?? "—"}</Text>
                    </InfoField>
                    <InfoField label="Tryb pracy">
                        <Text color="white" fontSize="sm">{workflow.workMode ?? "—"}</Text>
                    </InfoField>
                    <InfoField label="Poziom">
                        <Text color="white" fontSize="sm">{workflow.seniorityLevel ?? "—"}</Text>
                    </InfoField>
                    <InfoField label="Liczba wakatów">
                        <Text color="white" fontSize="sm">{workflow.vacancyCount ?? "—"}</Text>
                    </InfoField>
                    <InfoField label="Planowany start">
                        <Text color="white" fontSize="sm">{workflow.plannedStartDate ?? "—"}</Text>
                    </InfoField>
                    <InfoField label="Utworzono">
                        <Text color="white" fontSize="sm">{new Date(workflow.createdAtUtc).toLocaleString("pl-PL")}</Text>
                    </InfoField>
                    <InfoField label="Zaktualizowano">
                        <Text color="white" fontSize="sm">{new Date(workflow.updatedAtUtc).toLocaleString("pl-PL")}</Text>
                    </InfoField>
                    <InfoField label="Chatu">
                        <a
                            href={`https://cybejob-client-test-215277898238.europe-west1.run.app/chat${workflow.chatPath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "white", fontSize: 14 }}
                        >
                            Link do czatu
                        </a>
                    </InfoField>
                </HStack>
            </Box>

            <SectionCard title="Opis stanowiska">
                <VStack align="flex-start" gap={4} w="full">
                    <InfoField label="Krótki opis">
                        <Text color="white" fontSize="sm">{workflow.shortDescription ?? "—"}</Text>
                    </InfoField>
                    {workflow.goals && (
                        <InfoField label="Cele">
                            <Text color="white" fontSize="sm">{workflow.goals}</Text>
                        </InfoField>
                    )}
                    {workflow.typicalDay && (
                        <InfoField label="Typowy dzień">
                            <Text color="white" fontSize="sm">{workflow.typicalDay}</Text>
                        </InfoField>
                    )}
                    {workflow.teamStructure && (
                        <InfoField label="Struktura zespołu">
                            <Text color="white" fontSize="sm">{workflow.teamStructure}</Text>
                        </InfoField>
                    )}
                    {workflow.supervisor && (
                        <InfoField label="Przełożony">
                            <Text color="white" fontSize="sm">{workflow.supervisor}</Text>
                        </InfoField>
                    )}
                </VStack>
            </SectionCard>

            <SectionCard title="Zakres obowiązków">
                <VStack align="flex-start" gap={5} w="full">
                    <InfoField label="Główne obowiązki">
                        <StringList items={workflow.mainDuties} />
                    </InfoField>
                    <InfoField label="Odpowiedzialności">
                        <StringList items={workflow.responsibilities} />
                    </InfoField>
                </VStack>
            </SectionCard>

            <SectionCard title="Benefity i etapy rekrutacji">
                <VStack align="flex-start" gap={4} w="full">
                    <InfoField label="Benefity">
                        <TagList items={workflow.benefits} />
                    </InfoField>
                    <InfoField label="Narzędzia">
                        <TagList items={workflow.tools} />
                    </InfoField>
                    <InfoField label="Warunki pracy">
                        <Text color="white" fontSize="sm">{workflow.workConditions ?? "—"}</Text>
                    </InfoField>
                    <InfoField label="Etapy rekrutacji">
                        <StringList items={workflow.recruitmentStages} />
                    </InfoField>
                </VStack>
            </SectionCard>

            <JsonSection
                title="Wymagania"
                data={{ skills: workflow.skills, experienceRequirements: workflow.experienceRequirements, requiredDocuments: workflow.requiredDocuments, riskFactors: workflow.riskFactors, protectiveEquipment: workflow.protectiveEquipment }}
                downloadFilename={`requirements_${workflowId}.json`}
            />

            <JsonSection
                title="Pytania rekrutacyjne"
                data={{ customQuestions: workflow.customQuestions, motivationQuestions: workflow.motivationQuestions }}
                downloadFilename={`questions_${workflowId}.json`}
            />

            <JsonSection
                title="Warunki zatrudnienia"
                data={{ employmentForms: workflow.employmentForms, salaryModels: workflow.salaryModels, bonuses: workflow.bonuses, allowances: workflow.allowances, financialExpectations: workflow.financialExpectations, workingTime: workflow.workingTime }}
                downloadFilename={`employment_${workflowId}.json`}
            />

            <JsonSection
                title="Konfiguracja konwersacji"
                data={workflow.conversationConfig}
                downloadFilename={`conversation_config_${workflowId}.json`}
            />

            {SCORING_ENABLED && (
                <JsonSection
                    title="Konfiguracja scoringu"
                    data={workflow.scoringConfig}
                    downloadFilename={`scoring_config_${workflowId}.json`}
                />
            )}

            <JsonSection
                title="Reguły dyskwalifikacji"
                data={workflow.disqualificationRules}
                downloadFilename={`disqualification_rules_${workflowId}.json`}
            />

            <JsonSection
                title="Dostępność"
                data={workflow.availability}
                downloadFilename={`availability_${workflowId}.json`}
            />
        </VStack>
        {editModalValues && (
            <JobOfferFormModal
                onClose={() => setEditModalValues(null)}
                initialValues={editModalValues}
                onSubmit={async (values, isDraft = false) => {
                    await WorkflowService.update(workflowId, toUpdateRequest(values, { isDraft }));
                    if (!isDraft) setEditModalValues(null);
                    refetchWorkflow();
                }}
            />
        )}
        </>
    );
};

export default WorkflowDetailPage;
